import React, {useState } from 'react';
import styled from 'styled-components';
import {useNavigate, useLocation } from 'react-router-dom';
import {getAuth } from 'firebase/auth';
import {getFunctions, httpsCallable } from 'firebase/functions';

// Reuse your styled components from Login or create new ones
const VerificationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - ${({theme }) => theme.spacing.xxl} * 2);
  background-color: ${({theme }) => theme.colors.light};
  padding: ${({theme }) => theme.spacing.md};
`;

const VerificationForm = styled.form`
  background-color: ${({theme }) => theme.colors.white};
  padding: ${({theme }) => theme.spacing.xl};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.md};
`;

const Title = styled.h2`
  text-align: center;
  color: ${({theme }) => theme.colors.primary};
  margin-bottom: ${({theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: ${({theme }) => theme.colors.text};
  font-weight: bold;
`;

const Input = styled.input`
  padding: ${({theme }) => theme.spacing.sm};
  border: 1px solid ${({theme }) => theme.colors.textLight};
  border-radius: 4px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: ${({theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(${({theme }) => theme.colors.primary}, 0.2);
  }
`;

const Button = styled.button`
  padding: ${({theme }) => theme.spacing.md};
  background-color: ${({theme }) => theme.colors.primary};
  color: ${({theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({theme }) => theme.colors.secondary};
  }

  &:disabled {
    background-color: ${({theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;

const MessageContainer = styled.div<{type?: 'success' | 'error' }>`
  padding: ${({theme }) => theme.spacing.sm};
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  margin-bottom: ${({theme }) => theme.spacing.md};
  ${({type, theme }) => type === 'error' && `
    background-color: #ffe0e0;
    color: ${theme.colors.error || '#dc3545'};
    border: 1px solid ${theme.colors.error || '#dc3545'};
  `}
  ${({type, theme }) => type === 'success' && `
    background-color: ${theme.colors.success || '#e0ffe0'};
    color: ${theme.colors.success || '#28a745'};
    border: 1px solid ${theme.colors.success || '#28a745'};
  `}
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: ${({theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
  align-self: center;
  margin-top: ${({theme }) => theme.spacing.sm};

  &:hover {
    color: ${({theme }) => theme.colors.secondary};
  }

  &:disabled {
    color: ${({theme }) => theme.colors.textLight};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const OtpVerification: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const functions = getFunctions();
  
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  
  // Get email from location state or from auth user
  const email = location.state?.email || auth.currentUser?.email;
  
  if (!email) {
    // Redirect to login if no email is available
    navigate('/login');
    return null;
  }

  const verifyOtp = httpsCallable(functions, 'verifyOtpForLogin');
  const sendOtp = httpsCallable(functions, 'sendOtpForLogin');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setMessageType(undefined);
    setLoading(true);

    try {
      const result = await verifyOtp({email, otp });
      
      if (result.data && (result.data as any).success) {
        setMessage('Verification successful! Redirecting...');
        setMessageType('success');
        
        // Redirect to dashboard or home page after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (error: any) {
      console.error("OTP verification error:", error);
      setMessageType('error');
      
      switch (error.code) {
        case 'not-found':
          setMessage('OTP expired. Please request a new one.');
          break;
        case 'deadline-exceeded':
          setMessage('OTP has expired. Please request a new one.');
          break;
        case 'permission-denied':
          if (error.message.includes('attempts')) {
            setMessage('Too many attempts. Please request a new OTP.');
          } else {
            setMessage('Invalid OTP. Please try again.');
          }
          break;
        default:
          setMessage('Verification failed. Please try again.');
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setMessage(null);
    setMessageType(undefined);

    try {
      const result = await sendOtp({email });
      
      if (result.data && (result.data as any).success) {
        setMessage('New verification code sent! Please check your email.');
        setMessageType('success');
        
        // Disable resend button for 60 seconds
        setResendDisabled(true);
        setTimeout(() => {
          setResendDisabled(false);
        }, 60000);
      }
    } catch (error: any) {
      console.error("Error resending OTP:", error);
      setMessageType('error');
      setMessage('Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <VerificationContainer>
      <VerificationForm onSubmit={handleSubmit}>
        <Title>Verify Your Email</Title>
        {message && <MessageContainer type={messageType}>{message}</MessageContainer>}
        
        <p style={{textAlign: 'center' }}>
          We've sent a 6-digit verification code to <strong>{email}</strong>
        </p>
        
        <FormGroup>
          <Label htmlFor="otp">Verification Code</Label>
          <Input
            type="text"
            id="otp"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="123456"
            required
            disabled={loading}
          />
        </FormGroup>
        
        <Button type="submit" disabled={loading || otp.length !== 6}>
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
        
        <ResendLink 
          onClick={handleResendOtp} 
          disabled={resendDisabled || resendLoading}
        >
          {resendLoading ? 'Sending...' : resendDisabled ? 'Resend available in 60s' : 'Resend Code'}
        </ResendLink>
      </VerificationForm>
    </VerificationContainer>
  );
};

export default OtpVerification;